import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { axisBucketLabel, labelStride } from '../../../../../lib/portalReport/series';
import {
  GRID_OPACITY,
  LINE_HALO,
  LINE_STROKE,
  CLUB_PALETTE_DARK,
  CLUB_PALETTE_LIGHT,
  resolveChartForm,
  tooltipCeiling,
  tooltipSide,
  type FittedAxis,
  type GroupChartForm,
  type GroupChartModel,
} from '../../../../../lib/portalReport/groupCharts';
import { decimalsForStep, moneyFull, numberFull } from './format';

// CLIENT PORTAL §A — grouped volume bars on the right axis, a cost line per club
// on the left. The chart Scott's clients already read, with every location on it.
//
// ⚠️⚠️ EVERY DECISION ABOUT WHAT MAY BE DRAWN IS UPSTREAM, in `groupCharts.ts` —
// the axes, the colour slots, which clubs are drawable, and above all whether a
// cost axis EXISTS at all. This file draws what it is handed, so those rules stay
// assertable without jsdom. In particular it cannot draw a cost line without a
// `CostLayer`, and a `CostLayer` cannot be constructed without a resolved single
// currency.
//
// ⚠️⚠️ BOTH AXES ARE ZERO-BASED, and that is what disarms the second axis rather
// than the mark difference alone. `AxisScale.min` is typed as the literal `0`, so
// a floating baseline is not expressible.
//
// ⚠️⚠️ FIGURE AND GROUND IS DONE WITH WEIGHT, NOT ALPHA, AND THAT IS A MEASURED
// REVERSAL. A muted bar fill was the plan; measured at the density case it came
// back 1.83–2.48 against the card, under the 3:1 floor, so the bars are SOLID
// and the GRID is what recedes (`GRID_OPACITY`). The lines lead by carrying a
// heavier stroke over a card-coloured HALO, which lifts them above 104 bars
// without either mark losing contrast. See `groupCharts.ts` for the numbers.
//
// ⚠️ COLOURS ARE LITERALS, NOT CSS CUSTOM PROPERTIES. The eight-slot ramp is a
// VALIDATED SET whose adjacent-pair separation was measured in this exact order,
// so its members are not independently swappable the way `--rpt-chan-a` is, and
// putting them in the stylesheet would invite exactly that.

const W = 900;
const H = 300;
// ⚠️ Padding on BOTH sides: two axes, two sets of labels.
//
// ⚠️ THE LEFT GUTTER IS THE COST AXIS'S, so it collapses when there is no cost
// axis. Caught in a browser on the mixed-currency case: 58 units of empty left
// margin, with the bars starting well in from the edge for a label that was
// deliberately withheld. Reserving room for an axis that is not there reads as a
// rendering fault rather than as the deliberate refusal it is.
const PAD_TOP = 18;
const PAD_BOTTOM = 34;
const PAD_LEFT_WITH_COST = 58;
const PAD_LEFT_BARE = 12;
// ⚠️ AND THE RIGHT GUTTER IS THE VOLUME AXIS'S, by the same argument (§4d):
// `Lines` removes the bars, so the volume axis goes with them and its margin
// must go too, or the plot sits inset from the right for a label that is not
// there.
const PAD_RIGHT_WITH_VOLUME = 58;
const PAD_RIGHT_BARE = 12;
const IH = H - PAD_TOP - PAD_BOTTOM;

/** Fraction of a month's band left as air between neighbouring groups. */
const GROUP_GUTTER = 0.24;

export interface GroupBarChartProps {
  chart: GroupChartModel;
  buckets: ReadonlyArray<{ bucket: string; label: string }>;
  mode: 'light' | 'dark';
  /** ⚠️ The final bucket is in progress — hatched, never silently drawn as a fall. */
  partialFinalBucket: boolean;
  /**
   * Which layers this card draws (§4d).
   *
   * ⚠️ PER CARD, and the card owns the state — see `GroupChartForm` for why it
   * is not in the URL.
   */
  form: GroupChartForm;
}

interface Hover {
  index: number;
  /** The hovered column's centre, for the highlight band. */
  x: number;
  y: number;
  /** ⚠️ Where the TOOLTIP sits — the far side of the plot. See `tooltipSide`. */
  tipX: number;
  /**
   * How wide the tooltip may be, in px (§4, 2026-09-04). See `tooltipCeiling`.
   */
  tipMax: number;
}

/** Where a value sits, given an axis. */
// ⚠️ TAKES THE WIDER SHAPE (round 8 §5): the bars' axis is zero-based and the
// cost lines' is data-fitted, and `AxisScale` (min: 0) is assignable to
// `FittedAxis` (min: number) — so one projector serves both without a union.
// The arithmetic already read `axis.min`, so nothing here changes.
function projector(axis: FittedAxis): (v: number) => number {
  const span = axis.max - axis.min || 1;
  return (v) => PAD_TOP + IH - ((v - axis.min) / span) * IH;
}

/**
 * ⚠️⚠️ A NULL IS A BREAK, NEVER A JOIN. A club that was not being measured in a
 * month has no line through it — drawing across the gap would be a confident
 * claim about a period we know nothing about, the same rule the rest of this
 * report follows.
 */
function segments(values: ReadonlyArray<number | null>): number[][] {
  const runs: number[][] = [];
  let current: number[] = [];
  values.forEach((v, i) => {
    if (v === null) {
      if (current.length) runs.push(current);
      current = [];
    } else {
      current.push(i);
    }
  });
  if (current.length) runs.push(current);
  return runs;
}

export function GroupBarChart(props: GroupBarChartProps) {
  const { chart, buckets, mode, partialFinalBucket, form } = props;
  // ⚠️⚠️ THE DECISION IS UPSTREAM AND PURE. This file draws what it is told;
  // whether a layer may be drawn at all — and what to say when the chosen form
  // has nothing — is `resolveChartForm`, so it is assertable without jsdom.
  const layers = resolveChartForm(chart, form);
  const [hover, setHover] = useState<Hover | null>(null);
  // ⚠️ THE RENDERED WIDTH, because legibility is a pixel property — the same
  // reason `ReportTrendChart` measures it. Defaults to the viewBox width so the
  // first paint is the correct full-width answer rather than a blank axis.
  const [renderedWidth, setRenderedWidth] = useState(W);
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry?.contentRect.width ?? 0;
      if (w > 0) setRenderedWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const palette = mode === 'dark' ? CLUB_PALETTE_DARK : CLUB_PALETTE_LIGHT;
  const colourOf = useCallback(
    (slot: number) => palette[slot % palette.length] as string,
    [palette],
  );

  // ⚠️ The left gutter belongs to the COST axis, so it collapses when there is
  // none — otherwise the mixed-currency case renders with 58 units of empty
  // margin reserved for a label that was deliberately withheld.
  const padLeft = layers.showCost ? PAD_LEFT_WITH_COST : PAD_LEFT_BARE;
  // ⚠️ AND THE RIGHT GUTTER FOLLOWS THE VOLUME AXIS, so `Lines` does not leave
  // the plot inset from the right for a label that was removed.
  const padRight = layers.showBars ? PAD_RIGHT_WITH_VOLUME : PAD_RIGHT_BARE;
  const innerWidth = W - padLeft - padRight;
  const n = buckets.length;
  const band = n > 0 ? innerWidth / n : innerWidth;
  const yVolume = useMemo(() => projector(chart.volumeAxis), [chart.volumeAxis]);
  const yCost = useMemo(
    () => (chart.cost ? projector(chart.cost.axis) : null),
    [chart.cost],
  );
  const cx = useCallback((i: number) => padLeft + band * (i + 0.5), [band, padLeft]);

  const onMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const i = Math.floor(((e.clientX - rect.left) * (W / rect.width) - padLeft) / band);
      if (i < 0 || i >= n) {
        setHover(null);
        return;
      }
      // ⚠️⚠️ THE TOOLTIP GOES ON THE FAR SIDE OF THE PLOT, NOT UNDER THE
      // CURSOR. It lists EVERY ticked club, so at eight clubs it is a ~150px box
      // that landed squarely on the month being read. `tooltipSide` picks the
      // half the cursor is not in; the highlighted band still says which month.
      //
      // ⚠️ The SVG's own top edge stays the vertical anchor, so the tooltip
      // cannot rise over the controls above it — structural rather than
      // measured, and the trend chart's own decision.
      const side = tooltipSide(i, n);
      const frac = side === 'right' ? 0.78 : 0.22;
      const tipX = rect.left + rect.width * frac;
      setHover({
        index: i,
        x: rect.left + cx(i) * (rect.width / W),
        y: rect.top,
        tipX,
        // §4: the tooltip's ceiling — see `Hover.tipMax`.
        // ⚠️ `documentElement.clientWidth` EXCLUDES the vertical scrollbar and
        // `window.innerWidth` does not (measured: a 1024 window has a 1009px
        // layout viewport). The report always scrolls, so the difference is
        // real — and it is the whole reason this is not a `vw` rule.
        tipMax: tooltipCeiling(tipX, rect.width, document.documentElement.clientWidth),
      });
    },
    [band, cx, n],
  );

  if (layers.emptyReason) {
    return (
      <p className="rpt-note rpt-muted" data-testid="group-chart-empty">
        {layers.emptyReason}
      </p>
    );
  }

  const barWidth = (band * (1 - GROUP_GUTTER)) / chart.bars.length;
  const groupLeft = padLeft + (band * GROUP_GUTTER) / 2;
  const hatchId = `grp-partial-${chart.key}`;
  // ⚠️ From the axis STEP, so every tick on the axis has the same precision.
  const costTicks = chart.cost?.axis.ticks ?? [];
  const costDecimals = decimalsForStep(
    costTicks.length > 1 ? Math.abs((costTicks[1] as number) - (costTicks[0] as number)) : 1,
  );

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="img"
        aria-label={`${chart.volumeLabel} by month for each location, with ${chart.costLabel.toLowerCase()}`}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          {/* ⚠️ THE SAME HATCH THE CRM CARDS USE FOR AN IN-PROGRESS PERIOD. One
              treatment for one meaning; a second pattern would read as a second
              kind of caveat. */}
          <pattern id={hatchId} width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
            <line x1="0" y1="0" x2="0" y2="6" strokeWidth="2" style={{ stroke: 'var(--rpt-muted)' }} />
          </pattern>
        </defs>

        {/* ⚠️⚠️ THE PARTIAL FINAL MONTH IS KEPT AND MARKED. Scott's own example
            shows September as an unmarked near-zero sliver in both charts, which
            reads as a business-wide crash — the single most likely thing to make
            these charts lie. */}
        {partialFinalBucket && n > 0 && (
          <rect
            x={padLeft + band * (n - 1)}
            y={PAD_TOP}
            width={band}
            height={IH}
            fill={`url(#${hatchId})`}
            opacity={0.22}
            data-testid="partial-final-bucket"
          />
        )}

        {/* ⚠️ Zero-based gridlines from the VOLUME axis — ONE grid, not two. Two
            sets of gridlines on a two-axis chart is a lattice, and a reader
            cannot tell which line belongs to which scale.

            ⚠️ AND THE GRID IS WHAT RECEDES. Gridlines are not data, so they are
            not held to the 3:1 floor the marks are — which is why they are the
            right thing to weaken when the marks cannot be. */}
        {chart.volumeAxis.ticks.map((t, i) => (
          <line
            key={`g-${i}`}
            x1={padLeft}
            x2={W - padRight}
            y1={yVolume(t)}
            y2={yVolume(t)}
            strokeWidth={1}
            opacity={GRID_OPACITY}
            style={{ stroke: 'var(--rpt-rule)' }}
          />
        ))}

        {/* LEFT AXIS — cost. ⚠️ Drawn only when a `CostLayer` exists, which
            cannot happen without one resolved currency. */}
        {layers.showCost &&
          chart.cost &&
          yCost &&
          chart.cost.axis.ticks.map((t, i) => (
            <text
              key={`yl-${i}`}
              x={padLeft - 8}
              y={yCost(t) + 4}
              textAnchor="end"
              className="rpt-axis-label"
              style={{ fill: 'var(--rpt-muted)' }}
            >
              {/* ⚠️⚠️ ONE DECIMAL RULE FOR THE WHOLE AXIS, FROM ITS STEP. A
                  per-value rule printed `0.00 · 25 · 50` — caught in a browser,
                  and the same defect `moneyAxis`'s own comment records: an axis
                  with two precisions reads as a different measure halfway up.

                  ⚠️ NO SYMBOL ON THE TICKS. The axis TITLE carries the
                  disambiguated one (`A$` / `NZ$`); repeating a bare `$` here
                  would be the ambiguity this chart's dependency removed, put
                  back thirteen times per axis. */}
              {t.toFixed(costDecimals)}
            </text>
          ))}

        {/* RIGHT AXIS — volume. ⚠️ Absent in `Lines`, along with its gutter. */}
        {layers.showBars && chart.volumeAxis.ticks.map((t, i) => (
          <text
            key={`yr-${i}`}
            x={W - padRight + 8}
            y={yVolume(t) + 4}
            textAnchor="start"
            className="rpt-axis-label"
            style={{ fill: 'var(--rpt-muted)' }}
          >
            {Math.round(t)}
          </text>
        ))}

        {/* ⚠️⚠️ BOTH AXIS TITLES ARE ROTATED TITLES IN THE GUTTERS. Round 4's ran
            along the reference line and straight through the plotted marks — an
            axis title in the gutter cannot collide with anything.

            ⚠️⚠️ CENTRED ON THE FULL HEIGHT, NOT THE PLOT HEIGHT. It describes
            the whole axis, and the gutter is empty above and below the plot, so
            using `H / 2` costs nothing and buys 52 units (21%) of room for a
            long label. MEASURED: the longest label the registry can produce
            today, `Cost per internet lead (NZ$)`, is 188 units — against 248
            (plot) it had 60 units of headroom, and against 300 it has 112. A
            33-character brand override measured 222, which fitted either way
            but only just.

            ⚠️ FLAGGED: the reported truncation to `Cost per internet le` could
            NOT be reproduced at 1024 or 1440, in either mode — no clipping
            ancestor, the title 181 units inside a 248-unit span, and it scales
            uniformly with the viewBox so it cannot be width-dependent. This
            change makes it robust rather than fixing an observed clip. */}
        {layers.showCost && chart.cost && (
          <text
            transform={`translate(13 ${H / 2}) rotate(-90)`}
            textAnchor="middle"
            className="rpt-axis-label"
            style={{ fill: 'var(--rpt-muted)' }}
            data-testid="cost-axis-title"
          >
            {chart.cost.axisLabel}
          </text>
        )}
        {layers.showBars && (
          <text
            transform={`translate(${W - 12} ${H / 2}) rotate(90)`}
            textAnchor="middle"
            className="rpt-axis-label"
            style={{ fill: 'var(--rpt-muted)' }}
            data-testid="volume-axis-title"
          >
            {chart.volumeLabel}
          </text>
        )}

        {/* GROUPED BARS — one per club per month, receding. */}
        {layers.showBars && chart.bars.map((s, slot) =>
          s.values.map((v, i) =>
            v === null ? null : (
              // ⚠️ A measured ZERO draws a hairline rather than nothing, so
              // "measured and nothing happened" is distinguishable from "not
              // measured" — the latter has no bar at all.
              <rect
                key={`b-${s.accountId}-${i}`}
                x={groupLeft + band * i + barWidth * slot}
                y={yVolume(v) - (v === 0 ? 1 : 0)}
                width={Math.max(barWidth - 1, 0.6)}
                height={Math.max(PAD_TOP + IH - yVolume(v), v === 0 ? 1 : 0.6)}
                // ⚠️ THE SOLID RAMP VALUE. A muted fill was measured at 1.83–2.48
                // against the card, under the 3:1 floor; solid it is the
                // validated 3.01 light / 3.37 dark. See the module note.
                style={{ fill: colourOf(s.colorIndex) }}
              />
            ),
          ),
        )}

        {/* COST LINES — leading. */}
        {layers.showCost &&
          chart.cost &&
          yCost &&
          chart.cost.lines.map((line) =>
            segments(line.values).map((run, ri) => {
              const colour = colourOf(line.colorIndex);
              if (run.length === 1) {
                const i = run[0] as number;
                return (
                  <circle
                    key={`c-${line.accountId}-${ri}`}
                    cx={cx(i)}
                    cy={yCost(line.values[i] as number)}
                    r={3.5}
                    style={{ fill: colour }}
                  />
                );
              }
              const d = run
                .map((i, k) => `${k === 0 ? 'M' : 'L'}${cx(i)} ${yCost(line.values[i] as number)}`)
                .join(' ');
              return (
                <g key={`c-${line.accountId}-${ri}`}>
                  {/* ⚠️⚠️ A CARD-COLOURED HALO IS WHAT MAKES THE LINE LEAD. The
                      bars are solid (a muted fill failed 3:1 — see the module
                      note), so the line cannot lead by the bars receding; it
                      leads by cutting its own gap through them. */}
                  <path
                    d={d}
                    fill="none"
                    strokeWidth={LINE_HALO}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ stroke: 'var(--rpt-card)' }}
                  />
                  <path
                    d={d}
                    fill="none"
                    strokeWidth={LINE_STROKE}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ stroke: colour }}
                  />
                </g>
              );
            }),
          )}

        {buckets.map((p, i) => {
          const every = labelStride(buckets, band, renderedWidth / W);
          if (i % every !== 0) return null;
          return (
            <text
              key={`x-${i}`}
              x={cx(i)}
              y={H - 11}
              textAnchor="middle"
              className="rpt-axis-label"
              style={{ fill: 'var(--rpt-muted)' }}
            >
              {axisBucketLabel(p)}
            </text>
          );
        })}

        {hover && (
          <rect
            x={padLeft + band * hover.index}
            y={PAD_TOP}
            width={band}
            height={IH}
            rx={4}
            style={{ fill: 'var(--rpt-chip)' }}
            opacity={0.6}
          />
        )}
      </svg>

      {hover && (
        <GroupTooltip
          hover={hover}
          chart={chart}
          buckets={buckets}
          colourOf={colourOf}
          partialFinalBucket={partialFinalBucket}
          // ⚠️ The tooltip drops the cost half in `Bars`, so it describes the
          // chart on screen rather than the model behind it.
          showCost={layers.showCost}
          showBars={layers.showBars}
        />
      )}
    </div>
  );
}

/**
 * ⚠️ THE TOOLTIP CARRIES THE ABSOLUTE VALUES, with units and currency. The bars
 * are counts and the line is money in the axis's own currency, which is
 * guaranteed to be one currency because the layer exists.
 */
function GroupTooltip({
  hover,
  chart,
  buckets,
  colourOf,
  partialFinalBucket,
  showCost,
  showBars,
}: {
  hover: Hover;
  chart: GroupChartModel;
  buckets: ReadonlyArray<{ bucket: string; label: string }>;
  colourOf: (slot: number) => string;
  partialFinalBucket: boolean;
  showCost: boolean;
  showBars: boolean;
}) {
  const i = hover.index;
  const isPartial = partialFinalBucket && i === buckets.length - 1;
  const costOf = (accountId: string): number | null | undefined =>
    chart.cost?.lines.find((l) => l.accountId === accountId)?.values[i];

  return (
    <div
      // ⚠️ ITS OWN MODIFIER, so the trend chart's tooltip keeps the width it was
      // tuned for. A shared style chosen for one content shape gets borrowed by
      // a surface with a different one — which is exactly how this broke.
      className="rpt-tooltip rpt-tooltip--group"
      style={{
        position: 'fixed',
        left: hover.tipX,
        top: hover.y,
        // ⚠️⚠️ THE MEASURED PLOT WIDTH IS PASSED AS A CUSTOM PROPERTY, NOT AS
        // `maxWidth` (round 8 §4). The stylesheet takes the SMALLER of it and a
        // viewport ceiling, so the ceiling logic lives in CSS while the inline
        // style carries only the value that is unknowable until runtime.
        //
        // ⚠️ An inline `maxWidth` could not be capped from CSS at all: inline wins
        // over any rule short of `!important`, which is the trap documented in
        // CLAUDE.md's selector section.
        ['--rpt-tip-max' as string]: `${hover.tipMax}px`,
        transform: 'translate(-50%, 10px)',
        pointerEvents: 'none',
        zIndex: 60,
      }}
    >
      <div className="rpt-tooltip-head">
        {buckets[i]?.label}
        {isPartial ? ' · partial month' : ''}
      </div>
      <dl className="rpt-tooltip-rows">
        {chart.bars.map((s) => {
          const volume = s.values[i];
          const cost = costOf(s.accountId);
          return (
            <div key={s.accountId} className="rpt-tooltip-row">
              <dt>
                <span className="rpt-swatch" style={{ background: colourOf(s.colorIndex) }} />
                {s.name}
              </dt>
              {/* ⚠️ "not measured", never a zero. */}
              <dd>
                {showBars
                  ? volume === null || volume === undefined
                    ? 'not measured'
                    : `${numberFull(volume)} ${chart.volumeLabel.toLowerCase()}`
                  : ''}
                {/* ⚠️ `chart.cost &&` as well as `showCost`, because TypeScript
                    cannot see that `resolveChartForm` only sets `showCost` when
                    a `CostLayer` exists. A non-null assertion would compile and
                    would be the one shape that could throw here. */}
                {/* ⚠️⚠️ A WITHHELD PART-MONTH COST IS NOT A ZERO COST, and
                    the two reach this branch as the same `null`. Hovering the
                    hatched month read `no cost each`, which says the month was
                    free; the truth is that a ratio over a period in progress is
                    inflated and we removed it deliberately (§4a). Found by
                    hovering it in a browser. */}
                {showCost && chart.cost
                  ? isPartial
                    ? ` · cost not shown for a part-month`
                    : ` · ${
                        cost === null || cost === undefined
                          ? 'no cost'
                          : moneyFull(cost, chart.cost.currency)
                        } each`
                  : ''}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}
